import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Activity, ChevronDown, ChevronRight, Cpu, Info, Layers, Server, Settings2, User } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { InfoTip } from "../components/Erklaerung";
import { Badge, Card, KlasseBadge } from "../components/ui";
import { RoleName } from "../components/RoleName";
import { useApp } from "../../store/AppStore";
import { CLASS_LABEL, formatDateDe, formatWeight, weight } from "../../domain/rules";
import { personChain, drivingDriver, type ChainSystem } from "../../domain/analysis";
import { LizenzBadge } from "../components/Lizenz";
import { ruleHint } from "../../domain/ruleHints";
import { su01Of } from "../../domain/su01";
import { TRACE_META, traceWindowLabel } from "../../data/traceMeta";
import { useUsage, type UsageRow, type UsageStatus } from "../../data/usage";
import type { TraceState } from "../../domain/types";

const TRACE_TXT: Record<TraceState, string> = { "nie-genutzt": "nie genutzt", genutzt: "genutzt", "kein-trace": "kein Trace" };
const TRACE_TONE: Record<TraceState, "green" | "amber" | "gray"> = { "nie-genutzt": "green", genutzt: "amber", "kein-trace": "gray" };
import { PCE_VERSION, RULESET } from "../../data/pce-ruleset";
import type { Person } from "../../domain/types";

const TYPE_LABEL: Record<string, string> = {
  lager: "Lager",
  buero: "Büro",
  it: "IT",
  entwickler: "Entwickler",
  extern: "Extern",
  technisch: "Technisch",
};

/** Originalbezeichnung der greifenden Regel aus dem Regelwerk (rein informativ). */
function ruleClassName(obj: string, field: string, value: string): string | undefined {
  const f = field ?? "";
  const v = value ?? "";
  return RULESET.find((r) => r.obj === obj && (r.field ?? "") === f && (r.val ?? "") === v)?.ruleClass;
}


// ------------------------------------------------------------ Verwendungszeilen (Drill-down)
function NutzungZeilen({ status, rows, dataSource, mitRolle = false }: { status: UsageStatus; rows: UsageRow[]; dataSource: string; mitRolle?: boolean }) {
  if (dataSource !== "seed") return <div className="text-faint text-xs">Verwendungsdaten liegen nur im Seed-Export vor (nutzung_detail.csv).</div>;
  if (status === "laedt") return <div className="text-faint text-xs">Verwendungsdaten werden geladen …</div>;
  if (status === "fehlt") return <div className="text-faint text-xs">Keine Verwendungsdatei für dieses System (npm run seed:all).</div>;
  if (rows.length === 0) return <div className="text-faint text-xs">Keine Verwendungszeile im Zeitraum {traceWindowLabel()}.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="text-faint text-left uppercase tracking-wide">
            <th className="px-2 py-1 font-medium">Transaktion</th>
            <th className="px-2 py-1 font-medium">Programm</th>
            {mitRolle && <th className="px-2 py-1 font-medium">Einzelrolle</th>}
            <th className="px-2 py-1 font-medium">Berechtigungswert</th>
            <th className="px-2 py-1 text-right font-medium">zuletzt</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
              <td className="text-strong px-2 py-1 font-mono font-semibold">{r.tcode}</td>
              <td className="text-dim px-2 py-1 font-mono">{r.report}</td>
              {mitRolle && <td className="text-dim px-2 py-1 font-mono">{r.roleId}</td>}
              <td className="text-normal px-2 py-1 font-mono">{r.obj}{r.field ? ` · ${r.field} = ${r.val}` : ""}</td>
              <td className="tnum text-normal px-2 py-1 text-right">{formatDateDe(r.lastUsed)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-faint mt-1 text-[11px]">Quelle {TRACE_META.sourceShort} · Zeitraum {traceWindowLabel()} · keine Aufrufzählung (Statistik verdichtet)</div>
    </div>
  );
}

// ------------------------------------------------------------ Ein System der Person: Kette + Nutzung
function SystemPanel({ s, open, onToggle }: { s: ChainSystem; open: boolean; onToggle: () => void }) {
  const { ds, dataSource } = useApp();
  const [nutzungOpen, setNutzungOpen] = useState(false);
  const [rowOpen, setRowOpen] = useState<string | null>(null);
  const acc = ds.accounts.find((a) => a.id === s.accountId);
  const usage = useUsage(s.sid, s.client, acc?.username ?? "", s.trace === "genutzt" && dataSource === "seed" && (nutzungOpen || rowOpen !== null));
        const rowCount = s.composites.reduce((n, g) => n + g.singles.length, 0);
        return (
          <Card
            className="mb-4"
            title={
              <span className="flex items-center gap-2">
                <Server size={16} className="shrink-0" />
                <span className="font-mono">{s.sid}/{s.client}</span>
                {s.isDriving && <Badge tone="brand">treibendes System</Badge>}
                {!s.fueRelevant && <LizenzBadge model={s.licenseModel} />}
              </span>
            }
            subtitle={<span className="inline-flex items-center gap-1">{`${rowCount} lizenzrelevante Zeile${rowCount === 1 ? "" : "n"}`} <InfoTip>Gezeigt werden nur Werte mit Regeltreffer im PCE-Regelwerk. Die Rollen tragen weitere Objekte ohne Lizenzwirkung.</InfoTip></span>}
            right={s.fueRelevant ? <KlasseBadge cls={s.cls} /> : <Badge tone="gray">Engine-Nutzung — keine FUE-Klasse</Badge>}
          >
            <button
              onClick={onToggle}
              className="text-dim mb-1 inline-flex items-center gap-1.5 text-sm font-medium"
            >
              {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              {open ? "Kette einklappen" : "Kette einblenden"}
            </button>
            {s.trace === "genutzt" && (
              <button
                onClick={() => setNutzungOpen((v) => !v)}
                className="text-dim mb-1 ml-4 inline-flex items-center gap-1.5 text-sm font-medium"
              >
                <Activity size={15} style={{ color: "var(--color-amp-amber)" }} />
                {nutzungOpen ? "Nutzung ausblenden" : "Nutzung dieses Kontos"}
                {usage.status === "bereit" && <span className="tnum text-faint text-xs">· {new Set(usage.rows.map((r) => r.tcode)).size} Transaktionen</span>}
              </button>
            )}
            {nutzungOpen && (
              <div className="mb-3">
                <NutzungZeilen status={usage.status} rows={usage.rows} dataSource={dataSource} mitRolle />
              </div>
            )}

            {open &&
              (rowCount === 0 ? (
                <div className="text-faint mt-2 text-sm">
                  Keine tragende oder vermeidbare Einzelrolle — dieses System hält die Person nicht hoch.
                </div>
              ) : (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full min-w-[760px] border-collapse text-sm">
                    <thead>
                      <tr className="text-faint text-left text-xs uppercase tracking-wide">
                        <th className="px-3 py-2 font-medium">
                          <span className="flex items-center gap-1">
                            <Layers size={12} /> Sammelrolle
                          </span>
                        </th>
                        <th className="px-3 py-2 font-medium">Einzelrolle</th>
                        <th className="px-3 py-2 font-medium">Berechtigungswert</th>
                        <th className="px-3 py-2 font-medium">Regel (Step)</th>
                        <th className="px-3 py-2 font-medium">Klasse</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.composites.map((g) =>
                        g.singles.map((sr, j) => {
                          const rc = ruleClassName(sr.authObject, sr.field, sr.value);
                          const rk = sr.singleRoleId;
                          const subOpen = rowOpen === rk;
                          const groupExtra = g.singles.filter((x) => x.singleRoleId === rowOpen).length;
                          return (
                            <Fragment key={`${g.compositeRoleId ?? "direkt"}-${sr.singleRoleId}-${j}`}>
                            <tr
                              style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}
                              className={sr.isTop ? "bg-sunken" : ""}
                            >
                              {j === 0 && (
                                <td className="px-3 py-3 align-top" rowSpan={g.singles.length + groupExtra}>
                                  <div className="text-normal font-medium">
                                    {g.compositeRoleName ?? "direkt zugewiesen"}
                                  </div>
                                  {g.compositeRoleId && (
                                    <div className="text-faint mt-0.5 font-mono text-xs">{g.compositeRoleId}</div>
                                  )}
                                </td>
                              )}
                              <td className="px-3 py-3 align-top">
                                <Link to={`/rollen?rolle=${sr.singleRoleId}`} className="hover:underline" title="Rolle öffnen">
                                  <RoleName id={sr.singleRoleId} name={sr.singleRoleName} />
                                </Link>
                              </td>
                              <td className="px-3 py-3 align-top">
                                <div className="text-normal tnum font-mono text-[13px]">{sr.authObject}</div>
                                {sr.field ? (
                                  <div className="text-faint mt-0.5 font-mono text-xs">
                                    {sr.field} = {sr.value}
                                  </div>
                                ) : (
                                  <div className="text-faint mt-0.5 text-xs">Kennzeichen (kein Berechtigungsobjekt)</div>
                                )}
                                {/* Kontraintuitive Einstufungen aktiv erklären — nicht kommentarlos zeigen. */}
                                {sr.field && ruleHint({ obj: sr.authObject, field: sr.field, val: sr.value }) && (
                                  <div className="mt-1 max-w-xs text-[11px] leading-snug" style={{ color: "var(--color-amp-amber)" }}>
                                    {ruleHint({ obj: sr.authObject, field: sr.field, val: sr.value })}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-3 align-top">
                                <div className="text-normal tnum text-[13px]">
                                  {PCE_VERSION} · Step {sr.ruleStep ?? "—"}
                                </div>
                                {rc && <div className="text-faint mt-0.5 text-xs">{rc}</div>}
                              </td>
                              <td className="px-3 py-3 align-top">
                                <div className="flex flex-wrap items-center gap-2">
                                  <KlasseBadge cls={sr.forcedClass} outline={!sr.isTop} />
                                  {sr.isTop && (
                                    <span className="text-[11px] font-semibold" style={{ color: "var(--color-brand-600)" }}>
                                      tragend
                                    </span>
                                  )}
                                  {sr.effective && <Badge tone="gray">vermeidbar</Badge>}
                                  {!sr.effective && sr.heldBack && <Badge tone="gray">vermeidbar · zweiter Treiber hält</Badge>}
                                  {sr.trace === "genutzt" ? (
                                    <button type="button" onClick={() => setRowOpen(subOpen ? null : rk)} className="inline-flex items-center" title="Verwendung anzeigen">
                                      <Badge tone="amber">genutzt {subOpen ? "▾" : "▸"}</Badge>
                                    </button>
                                  ) : sr.trace ? (
                                    <Badge tone={TRACE_TONE[sr.trace]}>{TRACE_TXT[sr.trace]}</Badge>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                            {subOpen && (
                              <tr style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
                                <td colSpan={4} className="px-3 py-2">
                                  <NutzungZeilen status={usage.status} rows={usage.rows.filter((r) => r.roleId === rk)} dataSource={dataSource} />
                                </td>
                              </tr>
                            )}
                            </Fragment>
                          );
                        }),
                      )}
                    </tbody>
                  </table>
                </div>
              ))}
          </Card>
        );
}

export function Berechtigungen() {
  const { ds, ratio } = useApp();
  const [params, setParams] = useSearchParams();
  const [why, setWhy] = useState(false);
  const [openSystems, setOpenSystems] = useState<string[]>([]);

  // Beispielpersonen: bevorzugt singleSystemDriven (genau EIN System zieht hoch — die
  // klarste Demo für „welches System gibt den Ausschlag"), danach mehrsystemige Fälle.
  const examples = useMemo(() => {
    const withChain = ds.persons.filter((p) => (p.systemClasses?.length ?? 0) > 0 && p.drivers.length > 0);
    const single = withChain.filter((p) => p.singleSystemDriven).slice(0, 6);
    const multi = withChain.filter((p) => !p.singleSystemDriven).slice(0, 4);
    return [...single, ...multi];
  }, [ds.persons]);

  const requestedId = params.get("person");
  const requested = requestedId ? ds.persons.find((p) => p.id === requestedId) : undefined;
  // AP1: „keine Treiber" heißt nicht „nichts zu zeigen". Wer ausschließlich auf
  // Systemen mit eigener Lizenzmetrik arbeitet, hat sehr wohl eine Rollenkette —
  // sie erzeugt nur keinen FUE-Bedarf. Leer ist nur, wer gar kein System hat.
  const requestedEmpty = !!requested && (requested.systemClasses?.length ?? 0) === 0;
  /** Person ohne FUE-relevantes System (AP1) — Kette sichtbar, Bedarf null. */
  const ohneFueSystem = !!requested && !requestedEmpty && !(requested.systemClasses ?? []).some((sc) => sc.fueRelevant);
  const validRequested = requested && !requestedEmpty ? requested : undefined;

  const selected: Person | undefined = validRequested ?? examples[0];

  const buttonList = useMemo(() => {
    if (validRequested && !examples.some((p) => p.id === validRequested.id)) {
      return [validRequested, ...examples];
    }
    return examples;
  }, [examples, validRequested]);

  // Kette der gewählten Person (driving-first sortiert).
  const chain: ChainSystem[] = useMemo(() => (selected ? personChain(selected, ds) : []), [selected, ds]);
  const accByIdMain = useMemo(() => new Map(ds.accounts.map((a) => [a.id, a])), [ds]);
  const su01 = useMemo(() => (selected ? su01Of(selected, accByIdMain) : undefined), [selected, accByIdMain]);

  // Beim Personenwechsel: nur das ausschlaggebende System aufgeklappt.
  useEffect(() => {
    if (!selected) return;
    const driving = chain.find((s) => s.isDriving) ?? chain[0];
    setOpenSystems(driving ? [driving.accountId] : []);
    setWhy(false);
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const select = (id: string) => {
    const next = new URLSearchParams(params);
    next.set("person", id);
    setParams(next, { replace: true });
  };

  const toggleSystem = (accountId: string) =>
    setOpenSystems((prev) => (prev.includes(accountId) ? prev.filter((x) => x !== accountId) : [...prev, accountId]));

  if (!selected) {
    return (
      <div>
        <PageHeader route="/berechtigungen" />
        <Card>
          <div className="text-dim">Keine Beispielperson mit auswertbarer Kette gefunden.</div>
        </Card>
      </div>
    );
  }

  // BEFUND: ausschließlich die AUSSCHLAGGEBENDE Klasse. Das ist die Klasse des
  // treibenden Systems (= Person.licenseClass, R2 über alle Systeme). Klassen anderer
  // Systeme (z. B. Core Use) sind NICHT der Befund — sie werden vom Maximum überlagert.
  const personClass = selected.licenseClass;
  // Mehrere Konten im SELBEN System (Mandanten) — R1 gilt auch innerhalb eines Systems.
  const mandanten = (() => {
    const m = new Map<string, number>();
    for (const sc of selected.systemClasses ?? []) m.set(sc.sid, (m.get(sc.sid) ?? 0) + 1);
    return [...m].filter(([, n]) => n > 1).map(([sid, n]) => ({ sid, n }));
  })();
  const drivingSys = chain.find((s) => s.isDriving) ?? chain[0];
  // Gleichstand: alle FUE-Konten, die die Klasse der Person selbst erreichen — typisch bei mehreren
  // Mandanten desselben Systems mit derselben Rolle. Dann gibt NICHT ein System den Ausschlag.
  const tiedAccounts = chain.filter((s) => s.fueRelevant && s.cls === personClass);
  const tie = tiedAccounts.length > 1;
  const topDriver = drivingDriver(selected);
  const otherClasses = chain
    .filter((s) => !s.isDriving && s.fueRelevant)
    .map((s) => s.cls)
    .filter((c) => c !== "NotRelevant" && c !== personClass); // gleichauf ist kein „nur"

  return (
    <div>
      <PageHeader
        route="/berechtigungen"
        right={<KlasseBadge cls={personClass}>Befund: {CLASS_LABEL[personClass]}</KlasseBadge>}
      />

      {requestedEmpty && requested && (
        <div
          className="mb-4 flex items-start gap-2 rounded-xl px-4 py-3 text-sm"
          style={{ borderWidth: 1, borderColor: "var(--border-soft)", backgroundColor: "var(--surface-sunken)" }}
        >
          <Info size={16} className="text-faint mt-0.5 shrink-0" />
          <span className="text-dim">
            Für Person <span className="text-strong font-semibold">{requested.id}</span> liegen keine Konten vor —
            es gibt daher nichts abzuleiten. Unten sehen Sie stattdessen ein Beispiel.
          </span>
        </div>
      )}

      {ohneFueSystem && (
        <div
          className="mb-4 flex items-start gap-2 rounded-xl px-4 py-3 text-sm"
          style={{ borderWidth: 1, borderColor: "var(--border-soft)", backgroundColor: "var(--surface-sunken)" }}
        >
          <Info size={16} className="text-faint mt-0.5 shrink-0" />
          <span className="text-dim">
            <span className="text-strong font-semibold">{requested?.id}</span> arbeitet ausschließlich auf Systemen mit
            eigener Lizenzmetrik. Die Berechtigungen sind unten vollständig nachvollziehbar — sie erzeugen nur{" "}
            <span className="text-strong font-semibold">keinen FUE-Bedarf</span>, weil diese Systeme nicht über
            Berechtigungsklassen abgerechnet werden.
          </span>
        </div>
      )}

      {/* -------- Personenauswahl -------- */}
      <Card
        title="Person wählen"
        subtitle="Beispiele mit klarer Nachweiskette"
        className="mb-6"
      >
        <div className="flex flex-wrap gap-2">
          {buttonList.map((p) => {
            const active = p.id === selected.id;
            return (
              <button
                key={p.id}
                onClick={() => select(p.id)}
                className="tnum inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm"
                style={
                  active
                    ? { backgroundColor: "var(--color-brand-600)", color: "#fff", fontWeight: 600 }
                    : { borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-dim)" }
                }
              >
                <User size={14} className="shrink-0" />
                {p.id}
                {p.singleSystemDriven && (
                  <span
                    className="rounded px-1 text-[10px] font-semibold"
                    style={{
                      backgroundColor: active ? "rgba(255,255,255,0.25)" : "var(--surface-sunken)",
                      color: active ? "#fff" : "var(--text-faint)",
                    }}
                  >
                    1 System zieht hoch
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </Card>

      {/* -------- Befund (nur die ausschlaggebende Klasse) -------- */}
      <Card
        title={`Befund für ${selected.name ?? selected.id}`}
        subtitle={<span className="inline-flex items-center gap-1">Person → System → Rolle → Wert → Regel → Klasse <InfoTip>Die Klasse ist berechnet, nicht behauptet: jeder Berechtigungswert läuft gegen das PCE-Regelwerk {PCE_VERSION}, die erste greifende Regel (kleinster Step) gewinnt. Die Klasse der Person ist das Maximum über alle Systeme und Mandanten (R2).</InfoTip></span>}
        right={
          <KlasseBadge cls={personClass}>
            Gewicht {formatWeight(weight(personClass, ratio))} FUE
          </KlasseBadge>
        }
        className="mb-6"
      >
        <div
          className="rounded-xl px-4 py-4"
          style={{ borderWidth: 1, borderColor: "var(--border-soft)", backgroundColor: "var(--surface-sunken)" }}
        >
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="text-dim">Person</span>
            <span className="text-strong font-semibold">{selected.name ?? selected.id}</span>
            <span className="text-faint text-xs">
              ({selected.name ? `${selected.id} · ` : ""}{TYPE_LABEL[selected.type] ?? selected.type} · Einheit {selected.unit}{selected.department ? ` · ${selected.department}` : ""}{selected.location ? ` · ${selected.location}` : ""})
            </span>
            <span className="text-faint">wird eingestuft als</span>
            <KlasseBadge cls={personClass} />
          </div>

          {/* Befund als Kette, nicht als Absatz: jede Zeile eine Ebene. */}
          <div className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            <span className="text-faint text-xs uppercase tracking-wide">System</span>
            <span className="text-normal">
              {personClass === "NotRelevant" ? (
                <span className="inline-flex items-center gap-1">keines ausschlaggebend <InfoTip>Kein System dieser Person wird über Berechtigungsklassen abgerechnet, deshalb gibt es keinen FUE-Beitrag. Die Klassen je System stehen unten trotzdem. Wechselt ein System in eine FUE-Metrik, ist die Einstufung sofort da.</InfoTip></span>
              ) : tie ? (
                <span className="inline-flex flex-wrap items-center gap-1">
                  <span className="text-strong font-mono font-semibold">{drivingSys?.sid}/{drivingSys?.client}</span>
                  <span className="text-faint">stellvertretend · {tiedAccounts.length} Konten gleichauf</span>
                  <InfoTip>{tiedAccounts.map((s) => `${s.sid}/${s.client}`).join(", ")} liegen gleichauf auf {CLASS_LABEL[personClass]}. Jedes davon hielte die Klasse allein.</InfoTip>
                </span>
              ) : (
                <span className="text-strong font-mono font-semibold">{drivingSys?.sid}/{drivingSys?.client}</span>
              )}
            </span>
            {topDriver && (
              <>
                <span className="text-faint text-xs uppercase tracking-wide">Einzelrolle</span>
                <RoleName id={topDriver.singleRoleId ?? topDriver.roleId} name={topDriver.roleName} />
                <span className="text-faint text-xs uppercase tracking-wide">Objekt · Wert</span>
                <span className="text-strong font-mono">{topDriver.authObject}{topDriver.field ? ` · ${topDriver.field} = ${topDriver.value}` : ""}</span>
                <span className="text-faint text-xs uppercase tracking-wide">Regel</span>
                <span className="text-dim">PCE {PCE_VERSION} · Step {topDriver.ruleStep ?? "—"}</span>
              </>
            )}
            <span className="text-faint text-xs uppercase tracking-wide">Klasse</span>
            <span><KlasseBadge cls={personClass} /></span>
            <span className="text-faint text-xs uppercase tracking-wide">SU01</span>
            <span className="text-dim inline-flex items-center gap-1">
              {su01?.funktion ?? "—"} · {su01?.abteilung ?? "—"}
              <InfoTip>Funktion und Abteilung aus den SU01-Adressfeldern der Konten dieser Person ({su01?.gepflegt ?? 0} von {selected.accountIds.length} Konten gepflegt). Je System gepflegt, oft veraltet; der Personalstamm oben bleibt maßgeblich.{su01?.widerspruch && " Die Konten dieser Person nennen unterschiedliche Werte."}</InfoTip>
              {su01?.widerspruch && <Badge tone="amber">Konten widersprechen sich</Badge>}
            </span>
            {personClass !== "NotRelevant" && otherClasses.length > 0 && (
              <>
                <span className="text-faint text-xs uppercase tracking-wide">weitere Systeme</span>
                <span className="text-dim inline-flex items-center gap-1">{[...new Set(otherClasses)].map((c) => CLASS_LABEL[c]).join(", ")} <InfoTip>Nicht der Befund: nach R2 zählt allein die höchste Klasse, niedrigere werden überlagert und nicht getrennt lizenziert.</InfoTip></span>
              </>
            )}
            {mandanten.length > 0 && (
              <>
                <span className="text-faint text-xs uppercase tracking-wide">Mandanten</span>
                <span className="text-normal inline-flex items-center gap-1">{mandanten.map((m) => `${m.sid}: ${m.n} Konten in ${m.n} Mandanten`).join(" · ")} <InfoTip>Zählt als eine Person mit einem FUE, R1 gilt auch innerhalb eines Systems. Die Klasse ist das Maximum über alle Mandanten.</InfoTip></span>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* -------- Systeme nebeneinander -------- */}
      <Card
        title="Systeme der Person"
        subtitle={personClass === "NotRelevant" ? "Klasse je System · kein System ausschlaggebend" : "Klasse je System · treibendes System hervorgehoben"}
        className="mb-6"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {chain.map((s) => {
            const open = openSystems.includes(s.accountId);
            return (
              <button
                key={s.accountId}
                onClick={() => toggleSystem(s.accountId)}
                className="block rounded-xl px-4 py-3 text-left"
                style={{
                  borderWidth: s.isDriving ? 2 : 1,
                  borderColor: s.isDriving ? "var(--color-brand-600)" : "var(--border-soft)",
                  // AP6.1: theme-robust — mischt Brand mit der Kartenfläche, lesbar in hell UND dunkel.
                  backgroundColor: s.isDriving ? "color-mix(in srgb, var(--color-brand-500) 14%, var(--surface-card))" : "var(--surface-sunken)",
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-strong flex items-center gap-1.5 font-mono text-sm font-semibold">
                    <Server size={14} className="shrink-0" />
                    {s.sid}/{s.client}
                  </span>
                  {s.fueRelevant ? (
                    <KlasseBadge cls={s.cls} />
                  ) : (
                    <Badge tone="gray">{s.engines.length > 0 ? `Engine-Nutzung (${s.engines.join(", ")})` : "eigene Metrik"}</Badge>
                  )}
                </div>
                <div className="text-faint mt-1 text-xs">Konto {s.accountId}</div>
                {(() => { const a = accByIdMain.get(s.accountId); return a && (a.su01Function || a.su01Department) ? <div className="text-dim mt-0.5 text-xs">SU01: {a.su01Function ?? "—"} · {a.su01Department ?? "—"}</div> : <div className="text-faint mt-0.5 text-xs">SU01: nicht gepflegt</div>; })()}
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                  <Badge tone={TRACE_TONE[s.trace]}>{TRACE_TXT[s.trace]}</Badge>
                  <span className="text-faint">Trace {traceWindowLabel()}</span>
                </div>
                {/* Auf einem System mit eigener Metrik hat niemand eine FUE-Klasse — dort gibt es
                    Engine-Nutzung, sonst nichts. Die berechnete Klasse ist nur eine Nebeninformation. */}
                {!s.fueRelevant && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <LizenzBadge model={s.licenseModel} />
                    <span className="text-faint text-xs">keine FUE-Klasse — wäre ohne Engine-Metrik {CLASS_LABEL[s.cls]}</span>
                  </div>
                )}
                {s.fueRelevant && s.engines.length > 0 && (
                  <div className="text-faint mt-1 flex items-center gap-1 text-xs">
                    <Cpu size={12} className="shrink-0" /> zusätzlich Engine: {s.engines.join(", ")}
                  </div>
                )}
                {s.isDriving ? (
                  <div className="mt-2 text-xs font-semibold" style={{ color: "var(--color-brand-600)" }}>
                    gibt den Ausschlag ↑
                  </div>
                ) : personClass !== "NotRelevant" ? (
                  <div className="text-faint mt-2 text-xs">{tie && s.fueRelevant && s.cls === personClass ? "gleichauf — hielte die Klasse allein" : "nicht ausschlaggebend"}</div>
                ) : null}
                <div className="text-faint mt-2 flex items-center gap-1 text-[11px]">
                  {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  {open ? "Kette eingeblendet" : "Kette anzeigen"}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* -------- Kette je System (aufklappbar) -------- */}
      {chain.map((s) => (
        <SystemPanel key={`panel-${s.accountId}`} s={s} open={openSystems.includes(s.accountId)} onToggle={() => toggleSystem(s.accountId)} />
      ))}

      {/* -------- Warum diese Einstufung? -------- */}
      <Card className="mb-6">
        <button
          onClick={() => setWhy((v) => !v)}
          className="text-strong flex w-full items-center gap-2 text-left text-[15px] font-semibold"
        >
          {why ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          Warum diese Einstufung?
        </button>
        {why && (
          <div className="mt-4 space-y-3 text-sm">
            <div className="rounded-lg px-4 py-3" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
              <div className="text-strong text-sm font-semibold">R2 — höchste Klasse gewinnt</div>
              <p className="text-dim mt-1">
                Die Person hat {chain.length} System{chain.length === 1 ? "" : "e"}. Jedes Konto wird für sich klassifiziert.
                Das <span className="text-strong font-semibold">Maximum</span> über alle Systeme ergibt die Klasse der
                Person — nie die Summe. Hier zieht{" "}
                <span className="text-strong font-mono">{drivingSys?.sid}/{drivingSys?.client}</span> mit{" "}
                <span className="text-strong font-semibold">{CLASS_LABEL[personClass]}</span> nach oben.
              </p>
            </div>

            {topDriver && (
              <div className="rounded-lg px-4 py-3" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
                <div className="text-strong text-sm font-semibold">Die greifende Regel</div>
                <p className="text-dim mt-1">
                  Einzelrolle <RoleName id={topDriver.singleRoleId ?? topDriver.roleId} name={topDriver.roleName} /> enthält Objekt{" "}
                  <span className="text-strong font-mono">{topDriver.authObject}</span>
                  {topDriver.field ? (
                    <>
                      {" "}mit Wert <span className="text-strong font-mono">{topDriver.field} = {topDriver.value}</span>
                    </>
                  ) : null}{" "}
                  ⇒ <span className="text-strong font-semibold">{CLASS_LABEL[topDriver.forcedClass]}</span>. Herkunft:{" "}
                  <span className="text-strong">PCE-Regelwerk {PCE_VERSION}, Step {topDriver.ruleStep ?? "—"}</span>. Die
                  erste greifende Regel (kleinster Step) gewinnt.
                </p>
              </div>
            )}

            {selected.licenseClass !== selected.cleanClass && (
              <div
                className="rounded-lg px-4 py-3"
                style={{ borderWidth: 1, borderColor: "var(--border-soft)", backgroundColor: "var(--surface-sunken)" }}
              >
                <div className="text-strong text-sm font-semibold">R3 — vermeidbarer Treiber</div>
                <p className="text-dim mt-1">
                  Entfernt man die als <Badge tone="gray">vermeidbar</Badge> markierten Einzelrollen, fällt die Person auf
                  ihre Zielklasse <span className="text-strong font-semibold">{CLASS_LABEL[selected.cleanClass]}</span>{" "}
                  zurück. Ein Hebel senkt aber nur, wenn kein zweiter Treiber gleicher Klasse sie oben hält.
                </p>
              </div>
            )}

            <div className="text-faint text-xs">
              Klassifiziert, nicht behauptet: die Einstufung ist das Ergebnis der Regelprüfung, kein manuell gepflegter
              Lizenztyp.
            </div>
          </div>
        )}
      </Card>

      {/* -------- Einstellungen -------- */}
      <div
        className="flex flex-wrap items-center gap-2 rounded-xl px-4 py-3 text-sm"
        style={{ borderWidth: 1, borderColor: "var(--border-soft)", backgroundColor: "var(--surface-sunken)" }}
      >
        <Settings2 size={16} className="text-faint shrink-0" />
        <span className="text-dim">Die Klassen-Gewichte (Verhältnistabelle) stellen Sie in den Einstellungen ein.</span>
        <Link to="/einstellungen" className="ml-auto font-medium" style={{ color: "var(--color-brand-600)" }}>
          Zu den Einstellungen →
        </Link>
      </div>
    </div>
  );
}
